import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

const getScrollKey = (location) => `scroll-position:${location.pathname}${location.search}${location.hash}`;

function ScrollToTop() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const previousLocation = useRef(null);

  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  useEffect(() => {
    const scrollKey = getScrollKey(location);
    const savePosition = () => {
      window.sessionStorage.setItem(scrollKey, String(window.scrollY));
    };

    window.addEventListener("beforeunload", savePosition);

    return () => {
      savePosition();
      window.removeEventListener("beforeunload", savePosition);
    };
  }, [location]);

  useEffect(() => {
    const initialNavigation = previousLocation.current === null;
    const samePage = previousLocation.current?.pathname === location.pathname;
    const sameHash = previousLocation.current?.hash === location.hash;
    previousLocation.current = location;
    // Keep catalog controls in view for filter updates, but allow footer links
    // to return to the top even when their destination is the current page.
    if (samePage && sameHash && navigationType !== "POP" && !location.state?.scrollToTop) return;

    // BrowserRouter also reports POP on a fresh document load. Only restore a
    // saved position for Back/Forward or the browser's own Reload action;
    // freshly opened links should start at the top.
    const documentNavigationType = window.performance?.getEntriesByType?.("navigation")[0]?.type;
    const restorePosition = navigationType === "POP" && (
      !initialNavigation || documentNavigationType === "reload" || documentNavigationType === "back_forward"
    );

    if (restorePosition) {
      const savedPosition = window.sessionStorage.getItem(getScrollKey(location));

      if (savedPosition !== null && Number.isFinite(Number(savedPosition))) {
        const frame = window.requestAnimationFrame(() => window.scrollTo(0, Number(savedPosition)));
        return () => window.cancelAnimationFrame(frame);
      }
    }

    if (location.hash) {
      let targetId = location.hash.slice(1);
      try {
        targetId = decodeURIComponent(targetId);
      } catch {
        // A malformed fragment can still match a literal element ID.
      }
      const target = document.getElementById(targetId);
      if (target) {
        // Route content is committed before effects run. The next frame uses
        // its final layout and respects the target's fixed-header scroll margin.
        const frame = window.requestAnimationFrame(() => target.scrollIntoView({ block: "start", behavior: "instant" }));
        return () => window.cancelAnimationFrame(frame);
      }
    }

    window.scrollTo(0, 0);
  }, [location, navigationType]);

  return null;
}

export default ScrollToTop;
